/* eslint-disable arrow-body-style */
import type React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "./index.css";
import { LoadScript } from "@react-google-maps/api";
import NoMatch from "src/pages/NoMatch/NoMatch";
import { useAtomValue } from "jotai";
import { Suspense, lazy } from "react";
import "react-toastify/dist/ReactToastify.css";
import TopV2 from "src/features/Event/pages/TopV2/TopV2";
import { isLogin_atom } from "./features/Auth/auth.atom";
import { GOOGLE_MAP_API_KEY } from "./config";

const EventDetailsV2 = lazy(
    () => import("src/features/Event/pages/EventDetailsV2/EventDetailsV2")
);
const CreateEventV2 = lazy(
    () => import("src/features/Event/pages/CreateEventV2/CreateEventV2")
);
const Calendar = lazy(
    () => import("src/features/Event/pages/Calendar/Calendar")
);
const ProfileV2 = lazy(
    () => import("src/features/Profile/pages/ProfileV2/ProfileV2")
);
const ProfileRegistration = lazy(
    () =>
        import(
            "src/features/Profile/pages/ProfileRegistration/ProfileRegistration"
        )
);
const ArtistSearching = lazy(
    () => import("src/features/Profile/pages/ArtistSearching/ArtistSearching")
);
const ProfileEdit = lazy(
    () => import("src/features/Profile/pages/ProfileEdit/ProfileEdit")
);
const MapV2 = lazy(() => import("src/features/Map/pages/MapV2/MapV2"));
const BlockList = lazy(
    () => import("src/features/UserBlock/pages/BlockList/BlockList")
);
const Notification = lazy(
    () => import("src/features/Notification/pages/Notification/Notification")
);
const SettingV2 = lazy(
    () => import("src/features/Setting/pages/Setting/SettingV2")
);
const ArtistProfileV2 = lazy(
    () => import("src/features/Profile/pages/ArtistProfile/ArtistProfile")
);
const Following = lazy(
    () => import("src/features/Follow/pages/Following/Following")
);
const FollowerPage = lazy(
    () => import("src/features/Follow/pages/Follower/Follower")
);
const EditEvent = lazy(
    () => import("src/features/Event/pages/EditEvent/EditEvent")
);
const CreateEventOfCertified = lazy(
    () =>
        import(
            "src/features/Event/pages/CreateEventOfCertified/CreateEventOfCertified"
        )
);
const PasswordResetV2 = lazy(
    () => import("src/features/Auth/pages/PasswordResetV2/PasswordResetV2")
);
const SelectTypeV2 = lazy(
    () => import("src/features/Auth/pages/SelectTypeV2/SelectTypeV2")
);
const RegisterV2 = lazy(
    () => import("src/features/Auth/pages/RegisterV2/RegisterV2")
);
const LoginV2 = lazy(() => import("src/features/Auth/pages/LoginV2/LoginV2"));
const TermsAndPrivacyV2 = lazy(
    () => import("src/features/Auth/pages/TermsOfUseV2/TermsOfUseV2")
);
const ArtistAgreement = lazy(
    () => import("src/features/Auth/pages/ArtistAgreement/ArtistAgreement")
);
const ForgotPassword = lazy(
    () => import("src/features/Auth/pages/ForgotPassword/ForgotPassword")
);
const CheckEmail = lazy(
    () => import("src/features/Auth/pages/CheckEmail/CheckEmail")
);
const ResetPasswordWithToken = lazy(
    () =>
        import(
            "src/features/Auth/pages/ResetPasswordWithToken/ResetPasswordWithToken"
        )
);
const NotificationSettings = lazy(
    () =>
        import(
            "src/features/Setting/pages/NotificationSettings/NotificationSettings"
        )
);
const EditEventOfCertified = lazy(
    () =>
        import(
            "src/features/Event/pages/EditEventOfCertified/EditEventOfCertified"
        )
);

// const ChatList = lazy(
//     () => import("src/features/Chat/pages/ChatList/ChatList")
// );

// const Chat = lazy(() => import("src/features/Chat/pages/Chat/Chat"));

const App: React.FC = () => {
    const googleMapsApiKey = GOOGLE_MAP_API_KEY;

    const isLogin = useAtomValue(isLogin_atom);

    return (
        <LoadScript googleMapsApiKey={googleMapsApiKey!}>
            <BrowserRouter>
                {/* eslint-disable-next-line react/jsx-no-useless-fragment */}
                <Suspense fallback={<></>}>
                    <Routes>
                        <Route path="/" element={<TopV2 />} />
                        <Route path="/map" element={<MapV2 />} />
                        <Route
                            path="/artist-searching"
                            element={<ArtistSearching />}
                        />
                        <Route path="/event/:id" element={<EventDetailsV2 />} />
                        <Route
                            path="/artist/:id"
                            element={<ArtistProfileV2 />}
                        />
                        <Route
                            path="/terms-and-privacy"
                            element={<TermsAndPrivacyV2 />}
                        />

                        <Route
                            path="/profile-registration"
                            element={<ProfileRegistration />}
                        />
                        <Route
                            path="/login"
                            element={
                                isLogin ? <Navigate to="/" /> : <LoginV2 />
                            }
                        />
                        <Route
                            path="/forgot-password"
                            element={<ForgotPassword />}
                        />
                        <Route
                            path="/forgot-password/check-email"
                            element={<CheckEmail />}
                        />
                        <Route
                            path="/reset-password"
                            element={<ResetPasswordWithToken />}
                        />

                        {/* ログイン済みかどうかでルーティングを制御 */}
                        {isLogin ? (
                            <>
                                <Route
                                    path="/event/create"
                                    element={<CreateEventV2 />}
                                />

                                <Route
                                    path="/event/edit/:id"
                                    element={<EditEvent />}
                                />

                                <Route
                                    path="/select-type"
                                    element={<Navigate to="/" />}
                                />

                                <Route
                                    path="/profile"
                                    element={<ProfileV2 />}
                                />

                                <Route
                                    path="/profile/edit"
                                    element={<ProfileEdit />}
                                />

                                <Route
                                    path="/setting"
                                    element={<SettingV2 />}
                                />

                                <Route
                                    path="/follow/following"
                                    element={<Following />}
                                />

                                <Route
                                    path="/password-reset-v2"
                                    element={<PasswordResetV2 />}
                                />

                                <Route
                                    path="/follow/follower"
                                    element={<FollowerPage />}
                                />

                                <Route
                                    path="/calendar"
                                    element={<Calendar />}
                                />

                                <Route
                                    path="/block-list"
                                    element={<BlockList />}
                                />

                                <Route
                                    path="/event/create/certified"
                                    element={<CreateEventOfCertified />}
                                />

                                <Route
                                    path="/notification"
                                    element={<Notification />}
                                />

                                <Route
                                    path="event/certified/edit/:id"
                                    element={<EditEventOfCertified />}
                                />

                                <Route
                                    path="/password-change"
                                    element={<PasswordResetV2 />}
                                />

                                <Route
                                    path="/notification-settings"
                                    element={<NotificationSettings />}
                                />

                                {/* <Route
                                    path="/chat-list"
                                    element={<ChatList />}
                                />

                                <Route path="/chat/:id" element={<Chat />} /> */}
                            </>
                        ) : (
                            <>
                                <Route
                                    path="/event/create"
                                    element={<Navigate to="/" />}
                                />
                                <Route
                                    path="/event/edit/:id"
                                    element={<Navigate to="/" />}
                                />
                                <Route
                                    path="/select-type"
                                    element={<SelectTypeV2 />}
                                />
                                <Route
                                    path="/artist-agreement"
                                    element={<ArtistAgreement />}
                                />
                                <Route
                                    path="/register"
                                    element={<RegisterV2 />}
                                />
                                <Route
                                    path="/profile"
                                    element={<Navigate to="/login" />}
                                />
                                <Route
                                    path="/profile/edit"
                                    element={<Navigate to="/login" />}
                                />
                                <Route
                                    path="/setting"
                                    element={<Navigate to="/login" />}
                                />
                                <Route
                                    path="/follow/following"
                                    element={<Navigate to="/login" />}
                                />
                                <Route
                                    path="/password-reset-v2"
                                    element={<Navigate to="/login" />}
                                />

                                <Route
                                    path="/follow/follower"
                                    element={<Navigate to="/login" />}
                                />
                                <Route
                                    path="/block-list"
                                    element={<Navigate to="/" />}
                                />

                                <Route
                                    path="/calendar"
                                    element={<Navigate to="/" />}
                                />

                                <Route
                                    path="/create-v2/certified"
                                    element={<Navigate to="/" />}
                                />

                                <Route
                                    path="/notification"
                                    element={<Notification />}
                                />

                                <Route
                                    path="/event/certified/edit/:id"
                                    element={<Navigate to="/" />}
                                />

                                <Route
                                    path="/password-change"
                                    element={<Navigate to="/" />}
                                />

                                <Route
                                    path="/notification-settings"
                                    element={<Navigate to="/login" />}
                                />
                            </>
                        )}
                        <Route path="*" element={<NoMatch />} />
                    </Routes>
                </Suspense>
            </BrowserRouter>
        </LoadScript>
    );
};

export default App;
